import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { formatIsoDate } from '@src/shared/lib';
import { useCallback } from 'react';
import { selectAbsences } from '../../lib/absence';
import type { LoadedRange } from '../../lib/calendar';
import type { Absence, AbsenceDto } from '../../types';
import { absenceUrl } from './constants';

/**
 * How far either side of a day to ask for first. Generous on purpose: this
 * is one employee's rows, and coming back with the whole of an entry beats
 * trading another round trip for a shorter answer.
 */
const REACH_DAYS = 366;

/** How much further each unsuccessful round reaches. */
const REACH_GROWTH = 3;

/** 366, then 1098, then 3294 days either side -- about nine years. */
const MAX_ROUNDS = 3;

export type LoadedEntryRows = {
  absences: Absence[];
  /** The days asked for, so what came back can be told whether it is whole. */
  range: LoadedRange;
};

/**
 * A day so many days from ``day``, written the same way.
 *
 * Built from the parts rather than by parsing: ``new Date('YYYY-MM-DD')``
 * reads as UTC midnight, which is a different day from the one written
 * wherever the clock is not on UTC.
 */
function shiftDay(day: string, days: number): string {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  return formatIsoDate(new Date(year, month - 1, dayOfMonth + days));
}

/**
 * Fetch one employee's absences around a day, outside the calendar's window.
 *
 * The timeline loads what it draws, so an entry reaching past that is only
 * partly in hand -- and a reshape worked out from the visible fragment would
 * leave the rest of it booked on the days being moved away from. This asks
 * for enough either side to see the whole of it, without disturbing what the
 * calendar is showing or what it holds.
 *
 * ``isEnough`` decides when to stop, because what counts as enough differs:
 * an inferred entry needs its whole run, while an entry the pane named needs
 * only the rows behind the ids it gave. Where the first answer falls short
 * the reach grows and it asks again, so a run of any length is reachable
 * without every ordinary edit paying for the longest one.
 */
export default function useAbsenceEntry() {
  const apiClient = useAPIClient();

  return useCallback(
    async (
      userId: number,
      around: string,
      isEnough: (rows: Absence[], range: LoadedRange) => boolean,
    ): Promise<LoadedEntryRows | null> => {
      let reach = REACH_DAYS;
      let latest: LoadedEntryRows | null = null;

      for (let round = 0; round < MAX_ROUNDS; round += 1) {
        const range = {
          from: shiftDay(around, -reach),
          to: shiftDay(around, reach),
        };
        let rows: Absence[];
        try {
          rows = selectAbsences(
            (await apiClient.request({
              url: absenceUrl,
              payload: {
                user_id: String(userId),
                date_from: range.from,
                date_to: range.to,
              },
            })) as AbsenceDto[],
          );
        } catch {
          // Nothing to add to what the caller has, and nothing it can do with
          // a half answer: it stops and says so rather than submitting.
          return null;
        }

        latest = { absences: rows, range };
        if (isEnough(rows, range)) return latest;
        reach *= REACH_GROWTH;
      }

      // Still short after the last round. Answered anyway, so the caller sees
      // the same "not whole" it would have seen without asking at all.
      return latest;
    },
    [apiClient],
  );
}
